<template>
    <div class="fst-LlmsTemplatesEditor">
        <el-form ref="form" :model="model" :show-message="false" label-position="top">
            <el-form-item v-if="copy" prop="llm" :label="$t('llms.templates.editor.llm')" required>
                <LlmsSelect v-model="model.llm" @input="changeLlmsSelect()" />
            </el-form-item>
            <el-form-item prop="alias" :label="$t('llms.templates.editor.alias')" required>
                <el-input v-model="model.alias" clearable />
            </el-form-item>
            <el-tabs v-model="activeTab">
                <el-tab-pane name="0" :label="$t('llms.templates.editor.userPrompt')">
                    <el-form-item prop="data.userPrompt" >
                        <el-input v-model="model.data.userPrompt" type="textarea" :autosize="{minRows: 14, maxRows:20}" />
                    </el-form-item>
                </el-tab-pane>
                <el-tab-pane name="1" :label="$t('llms.templates.editor.systemPrompt')">
                    <el-form-item prop="data.systemPrompt">
                        <el-input v-model="model.data.systemPrompt" type="textarea" :autosize="{minRows: 14, maxRows:20}" />
                    </el-form-item>
                </el-tab-pane>
                <el-tab-pane name="2" :label="$t('llms.templates.editor.script')">
                    <el-form-item class="script" prop="data.script">
                        <MonacoEditor class="styled" ref="monacoEditor" :value="model.data.script" language="iqp_llm_completion_template" @change="v => model.data.script = v" />
                    </el-form-item>
                </el-tab-pane>
            </el-tabs>
        </el-form>
        <el-button class="d-none" type="primary" :disabled="!hasChangedFields">{{ $t('save') }}</el-button>
    </div>
</template>

<script>
    import _ from 'lodash';
    import { mapActions } from 'vuex';
    import MonacoEditor from '@/commons/components/monaco-editor/MonacoEditor';
    import { templates } from './Llms';
    import LlmsSelect from "./LlmsSelect.vue";
    import LlmsTemplatesCheck from './LlmsTemplatesCheck.vue';

    export default {
        name: "LlmsTemplatesEditor",
        components: {LlmsSelect, MonacoEditor},
        props: {
            id: [Number, String],
            copy: Boolean,
            templates: Array,
            llm: {
                type: Object,
                default: () => ({})
            }
        },
        data() {
            this.defaultModel = {
                llm: this.llm,
                alias: '',
                data: {
                    userPrompt: '',
                    systemPrompt: '',
                    script: templates.defaultScript
                }
            };
            return {
                model: _.cloneDeep(this.defaultModel),
                value: {},
                activeTab: '0'
            }
        },
        computed: {
            hasChangedFields() {
                let value = !_.isEqual(this.defaultModel, this.model);
                this.$emit('hasChangedFields', value);
                return value;
            }
        },
        mounted() {
            this.getTemplates({
                request: {count: 1, filter: {ids: [this.id]}},
                prepare: {target: this.$el}
            }).then(data => {
                this.value = data.list[0];
                this.setData(this.value);
                if (this.copy) {
                    this.changeLlmsSelect(this.templates);
                }
            });
        },
        methods: {
            ...mapActions('llm', ['getTemplates','saveTemplate']),
            save() {
                return new Promise(resolve => {
                    this.$refs.form.validate(valid => {
                        if (valid) {
                            let model = _.merge({}, this.model, {
                                data: {script: this.$refs.monacoEditor._getValue()}
                            });
                            if (this.copy) delete model.id;
                            //console.log('save', model); return;
                            this.saveTemplate({request: model}).then(data => {
                                this.setData(data);
                                this.$message({message: this.$t('llms.templates.editor.msg.success'), type: 'success', showClose: true});
                                resolve(data);
                            });
                        }
                    });
                });
            },
            check() {
                this.$modal.dialog(LlmsTemplatesCheck, {
                    title: this.$t('llms.templates.check.title'),
                    size: 'sm',
                    modalParams: {clickToClose: false},
                    ':template': this.model,
                    ':llm': this.llm,
                    buttons: [{
                        name: this.$t('check'),
                        type: 'primary',
                        handler: ({component}) => {
                            component.apply();
                        }
                    }, 'close']
                });
            },
            setData(value, clear) {
                if (clear) {
                    this.model = _.cloneDeep(this.defaultModel);
                } else {
                    this.model = _.merge({}, this.model, value);
                    this.defaultModel = _.cloneDeep(this.model);
                }
            },
            changeLlmsSelect(templates) {
                (templates ? Promise.resolve(templates) : this.getTemplates({
                    request: {count: 1000, filter: {scriptIds: [this.model.llm.id]}},
                    prepare: {target: this.$el}
                }).then(data => data.list)).then(list => {
                    let regExp = new RegExp(`^${this.value.alias}_\\d+$`);
                    let hasSameAlias = _.some(list, ['alias', this.value.alias])
                    let sameAliasNum = _.chain(list).map(item => {
                        if (item.alias.match(regExp)) {
                            let nums = item.alias.match(/\d+$/);
                            if (nums.length) {
                                return Number(nums[0]);
                            }
                        }
                    }).max().value();
                    if (hasSameAlias || sameAliasNum) {
                        if (sameAliasNum) sameAliasNum++;
                        this.model.alias = `${this.value.alias}_${sameAliasNum || 1}`;
                    } else {
                        this.model.alias = this.value.alias;
                    }
                });
            }
        }
    }
</script>

<style lang="scss">
    .fst-LlmsTemplatesEditor {
        height: 100%;
        .el-form {
            height: 100%;
            display: flex;
            flex-direction: column;
            &-item {
                margin-bottom: 0 !important;
                display: flex;

                >label{
                    margin-right: 10px;
                }
                &__content {
                    flex: 1;
                }

                &.script {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    margin-bottom: 0 !important;

                    .el-form-item__content {
                        min-height: 200px;
                        display: block;

                    }
                }
                .monaco-editor, .el-textarea__inner {
                    height: 50vh !important;
                    min-height: 250px !important;
                }


            }
        }
    }
</style>
